// Onboarding data schemas. Persisted server-side as a single pref-storage key
// ("onboarding.state") so it survives localStorage.clear() (the Settings
// "reset app" action) and follows the user across devices.

export type AssetClassId =
  | "equities"
  | "fx"
  | "fixed_income"
  | "energy"
  | "metals"
  | "crypto"
  | "macro";

export type OnboardingStep = "profile" | "concierge" | "watchlists" | "dashboard";

/**
 * The durable "have they ever done this" record.
 *
 * Four of these are now driven by the rail instead of a checklist row, but the
 * ids stay: the rail cursor answers "where are they in the run", this answers
 * "did it ever happen", and only the second survives the rail being dismissed.
 * useChecklistDetection keeps writing all six.
 */
export type ChecklistTaskId =
  | "popout_widget"
  | "create_watchlist"
  | "apply_filter"
  | "set_notification"
  | "listen_squawk"
  | "download_app";

export type RailStepId =
  | "squawk"
  | "chat"
  | "watchlist"
  | "highlight"
  | "popout";

/**
 * The guided run a trialist walks after the welcome modal closes.
 *
 * `stepIds` is resolved ONCE at beginRail and stored, rather than recomputed
 * per read the way the demo guide does it. The demo can recompute safely
 * because its variant key (assetClass) is frozen for the run; ours is live
 * access, which derives from a Keycloak token that can change mid-session.
 * A visible set that shifted under a live cursor would leave `step` pointing at
 * a different step and `completedStepIds` meaning nothing.
 *
 * Storing it also keeps this module — and the store that writes it — free of
 * any access-control import, which is what makes both node-testable.
 */
export type OnboardingRailState = {
  /** The steps THIS user's rail runs. Never recomputed once written. */
  stepIds: RailStepId[];
  /**
   * Index into stepIds of the step being asked for.
   *
   * May end one past the last index — advanceRailStep writes the incremented
   * value even on the last step. Readers clamp with Math.min.
   */
  step: number;
  /**
   * Steps actually done, in order. Skipped steps are absent, which is what
   * makes `steps_completed` on flow_complete mean something. It also makes
   * flow_step's completed-vs-skipped accounting idempotent without a
   * session-scoped set.
   */
  completedStepIds: RailStepId[];
  /** Set when the cursor walks past the last step. */
  completedAt?: string;
  /** Closed without finishing. Progress is kept; the checklist offers Resume. */
  exitedAt?: string;
};

export type OnboardingAnswers = {
  assetClasses: AssetClassId[];
  /**
   * Free-text "what do you trade / want to monitor". Kept verbatim — this is
   * the LLM concierge's input; never parsed client-side.
   */
  freeText?: string;
  /**
   * Tickers the user actually holds, typed or uploaded on step 1.
   *
   * Unlike `freeText` this is unambiguous, so it is treated as the strongest
   * signal we have: it becomes a real watchlist on both routes, and the
   * concierge is told about it rather than left to infer names from prose.
   */
  tickers?: string[];
};

export type OnboardingState = {
  /**
   * 2 added `rail`. No migrator ships with it: `rail` is optional and every
   * reader defaults it, so a row written at version 1 keeps working and nobody
   * with a completed onboarding gets re-triggered. The bump is a discriminator
   * for a future migration, not a migration itself.
   */
  version: 2;
  /** Written the moment the gate first fires — makes triggering idempotent. */
  startedAt: string;
  /** Set when the user finishes the final step. */
  completedAt?: string;
  /** Set when the user clicks "Skip for now". */
  dismissedAt?: string;
  /** Resume point across sessions. */
  lastStep?: OnboardingStep;
  answers?: OnboardingAnswers;
  /** NEWSQUAWK portfolio IDs chosen in the watchlist step. */
  selectedWatchlistIds?: number[];
  /** Which path built their dashboard: a template id or "concierge". */
  appliedTemplateId?: string;
  /**
   * The guided run. Absent until beginRail mints it, which happens after the
   * welcome modal completes — so every reader must default it.
   */
  rail?: OnboardingRailState;
  checklist: {
    dismissedAt?: string;
    /** taskId -> ISO completion timestamp. Absent = not done. */
    tasks: Partial<Record<ChecklistTaskId, string>>;
  };
};

export const ONBOARDING_STATE_KEY = "onboarding.state";

/**
 * Deliberately does NOT seed `rail`. The welcome flow runs first and the rail
 * is minted at beginRail, once squawk playability is known — seeding an empty
 * one here would make "has the rail started" unanswerable.
 */
export function createInitialOnboardingState(): OnboardingState {
  return {
    version: 2,
    startedAt: new Date().toISOString(),
    lastStep: "profile",
    checklist: { tasks: {} },
  };
}
